<template>
  <div
    id="formData"
    class="form-view container pb-5"
    @keydown.enter="saveAndNavigate"
  >
    <Breadcrumb />
    <Wizzard current-page="form" />
    <h2>Emissão de Certificado</h2>

    <p v-if="form.isPj && !form.isSSL">
      Agora é a hora de inserir os dados da
      <span class="fw-600">empresa</span> do solicitante
    </p>
    <p v-if="!form.isPj && !form.isSSL">
      Agora é a hora de inserir os dados
      <span class="fw-600">pessoais</span> do solicitante
    </p>
    <p v-if="form.isSSL">
      Agora é hora de inserir os dados do Certificado Digital
    </p>

    <div class="card p-2 d-flex flex-row flex-wrap">
      <div class="ms-2">
        <span class="fw-600">Voucher</span>: {{ getSolicitation.voucher }}
      </div>
      <div class="ms-2">
        <span class="fw-600">Certificado Adquirido</span>: {{ getProduto.nome }}
      </div>
    </div>

    <DynamicForms
      :key="dynamicCount"
      ref="dynamicComponent"
      :fields="form.fields"
      :external-errors="{ email: emailOwnership.fieldError }"
      @isValid="handleIsValid"
      @field-change="handleFieldChange"
    />

    <div class="d-flex justify-content-end my-4 row p-2">
      <button
        class="col-sm-12 col-md-2 btn"
        :class="
          !isValid || emailOwnership.isVerifying
            ? 'btn-secondary'
            : 'btn-primary'
        "
        type="button"
        :disabled="!isValid || emailOwnership.isVerifying"
        @click="saveAndNavigate"
      >
        <span class="me-2">Avançar</span>
        <Icon name="arrow-right" />
      </button>
    </div>

    <Contact />

    <ModalEmailOwnershipBlocked
      :show="emailOwnership.showBlockedModal"
      :on-close="emailOwnership.closeBlockedModal"
    />
    <ModalEmailOwnershipOtp
      :show="emailOwnership.showOtpModal"
      :email="dynamicComponent?.model?.email || ''"
      :otp-prefix="emailOwnership.otpPrefix"
      :loading="emailOwnership.isVerifying"
      :error-message="emailOwnership.otpError"
      :on-close="emailOwnership.closeOtpModal"
      @submit="emailOwnership.submitOtp"
      @resend="emailOwnership.resendCode"
      @change-email="emailOwnership.closeOtpModal"
    />
    <ModalEmailOwnershipGenericError
      :show="emailOwnership.showGenericErrorModal"
      :message="emailOwnership.genericErrorMessage"
      :on-close="emailOwnership.closeGenericErrorModal"
    />
  </div>
</template>

<script lang="ts">
  import { ComponentOptions, defineComponent, ref } from 'vue'

  import Contact from '@/components/Molecules/Contact.vue'
  import Icon from '@/components/Atoms/Icon.vue'
  import Breadcrumb from '@/components/Molecules/Breadcrumbs.vue'
  import Wizzard from '@/components/Molecules/Wizzard.vue'
  import DynamicForms from '@/components/Molecules/DynamicForms.vue'
  import ModalEmailOwnershipBlocked from '@/components/templates/ModalEmailOwnershipBlocked.vue'
  import ModalEmailOwnershipOtp from '@/components/templates/ModalEmailOwnershipOtp.vue'
  import ModalEmailOwnershipGenericError from '@/components/templates/ModalEmailOwnershipGenericError.vue'

  import { mapActions, mapState } from 'pinia'
  import { useSolicitation } from '@/store/Pinia/useSolicitation.store'
  import {
    FieldType,
    dynamicFormsController,
  } from '@/infra/sisAr/dynamicForms.controller'
  import { useRouteBirdId } from '@/store/Pinia/useRouteBirdId.store'
  import {
    FormDataType,
    useDynamicForms,
  } from '@/store/Pinia/useDynamicForms.store'
  import { useAR } from '@/store/Pinia/useAR.store'
  import { useEmailOwnershipVerification } from '@/composables'
  import { RemoveMaskCPF } from '@/utils/formatDocument'

  export default defineComponent({
    name: 'FormaData',
    components: {
      Breadcrumb,
      Wizzard,
      Contact,
      Icon,
      DynamicForms,
      ModalEmailOwnershipBlocked,
      ModalEmailOwnershipOtp,
      ModalEmailOwnershipGenericError,
    },
    setup() {
      const dynamicComponent = ref<ComponentOptions | null>(null)
      const emailOwnership = useEmailOwnershipVerification()

      return { dynamicComponent, emailOwnership }
    },
    data: () => ({
      form: {
        codVoucher: '',
        fields: [{}] as FieldType[],
        isPj: false,
        isSSL: false,
        cei: '',
        pis: '',
      },
      model: {
        cei: false,
        pis: false,
      },
      isValid: false,
      dynamicCount: 0,
    }),
    methods: {
      ...mapActions(useDynamicForms, ['setFormData', 'setFormInfo']),
      ...mapActions(useRouteBirdId, ['setRouterDone']),
      async saveAndNavigate() {
        if (!this.isValid) {
          return
        }

        const modelDynamic = this.$refs.dynamicComponent as ComponentOptions
        const model = modelDynamic.model as FormDataType

        const verified = await this.emailOwnership.verify({
          email: model.email,
          identity: RemoveMaskCPF(this.formData.cpf || ''),
          recipientFullName: this.formData.nome || '',
        })

        if (!verified) {
          return
        }

        this.setFormData(model)
        this.$router.push('/birdId/pass')
        this.setRouterDone('form')
      },
      handleIsValid(value: boolean) {
        this.isValid = value
      },
      handleFieldChange(fieldId: string, value: string) {
        if (fieldId === 'email') {
          this.emailOwnership.updateCurrentEmail(value)
        }
      },
    },
    computed: {
      ...mapState(useDynamicForms, ['formData']),
      ...mapState(useSolicitation, ['getProduto', 'getSolicitation']),
    },
    mounted() {
      this.form.isPj = this.getSolicitation.isPj
      this.form.isSSL = this.getSolicitation.isSSL
      this.form.cei = this.getProduto.cei
      this.form.pis = this.getProduto.pis
    },
    async beforeCreate() {
      const solicitation = useSolicitation()
      const produto = solicitation.getProduto
      const ar = useAR()
      const model = {
        productId: produto.id,
        sku: produto.sku,
        arsubdomain: ar.getSubdomain,
      }

      const { fields } = await dynamicFormsController.getFields(model)
      let formattedFields = [...fields]
      const produtoFinanceiroValue = String(
        this.formData.qsaResponseData?.produto_financeiro ?? '',
      ).toLowerCase()
      const hasFinancialProduct =
        produtoFinanceiroValue === 'true' || produtoFinanceiroValue === '1'
      const isRazaoSocialEmpty = !this.formData.razao_social?.trim()
      const shouldEnableRazaoSocialEdit =
        isRazaoSocialEmpty || hasFinancialProduct

      formattedFields = formattedFields.map((x: FieldType) => {
        if (x.id === 'razao_social') {
          return {
            ...x,
            disable: !shouldEnableRazaoSocialEdit,
          }
        }

        if (this.formData.cnpj && x.id === 'cnpj') {
          return {
            ...x,
            disable: true,
          }
        }
        return { ...x }
      })

      this.form.fields = formattedFields as FieldType[]

      const fieldsInfo = fields.map((field: FieldType) => ({
        id: field.id,
        icon: field.icon,
        label: field.label,
      }))

      this.setFormInfo(fieldsInfo)
      this.dynamicCount += 1
    },
    beforeRouteEnter(to, from, next) {
      const routes = useRouteBirdId()
      const solicitation = useSolicitation()
      const routesDone = routes.routes
      if (!solicitation.getSolicitation.voucher || !routesDone.voucher) {
        return next({ path: '/birdId/voucher' })
      }
      return next()
    },
  })
</script>

<style lang="stylus" scoped>
  @import './style.modules.styl';
</style>
